import { IllustrationIdea } from "@/assets/Illustrations";
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { Link, LinkProps, Outlet, useSearch } from "@tanstack/react-router";
import { ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { GenericErrorBoundary } from "../Layout/ErrorBoundary/ErrorBoundary";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Input, InputGroup } from "@/components/catalyst/input";
import { cn } from "@/lib/utils";
import { Button } from "@/components/catalyst/button";

export type SidebarListItemProps<T> = {
  model: T;
};

type SidebarListProps<T> = {
  onClearFilters?: () => void;
  createRoute?: LinkProps["to"];
  listRoute: LinkProps["to"];
  search: string;
  onSearchChange: (search: string) => void;
  fetchNextPage: () => void;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  totalCount: number;
  items?: T[];
  itemNode: (item: T) => ReactNode;
  linkProps: (item: T) => LinkProps;
  filters?: ReactNode;
};

export function SidebarList<T>({
  createRoute,
  listRoute,
  search,
  onSearchChange,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
  totalCount,
  itemNode,
  linkProps,
  items = [],
  filters,
}: SidebarListProps<T>) {
  const { ref, inView } = useInView();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  return (
    <div className="flex h-full">
      <aside
        className={cn(
          "relative h-full min-h-0 shrink-0 flex-col transition-all duration-300 ease-in-out",
          isCollapsed ? "w-14" : "w-80",
          "hidden 2xl:flex",
        )}
      >
        <header className="shadow-xs flex min-h-[64px] w-full items-center justify-between border-b border-gray-200 bg-white">
          <div
            className={cn(
              "flex w-full items-center space-x-2 px-3 py-2",
              isCollapsed && "justify-center",
            )}
          >
            {!isCollapsed ? (
              <InputGroup>
                {isFetching ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <MagnifyingGlassIcon
                    className="h-4 w-4 text-gray-400"
                    aria-hidden="true"
                  />
                )}
                <Input
                  name="search"
                  type="text"
                  autoComplete="off"
                  placeholder={`Search ${totalCount} results`}
                  value={search}
                  onChange={(e) => {
                    onSearchChange(e.target.value);
                  }}
                />
              </InputGroup>
            ) : (
              <button
                title="Search"
                onClick={() => setIsCollapsed(false)}
                className="rounded-full p-1.5 transition duration-200 ease-in-out hover:bg-gray-100"
              >
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}

            {createRoute && !isCollapsed ? (
              <div className="flex items-center">
                <Link title="Create new" to={createRoute} viewTransition>
                  <div className="focus:outline-hidden rounded-full p-1.5 transition duration-200 ease-in-out hover:bg-blue-100 focus:ring-2 focus:ring-blue-500">
                    <PlusIcon className="h-5 w-5" />
                  </div>
                </Link>
              </div>
            ) : null}
          </div>

          <Button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="mx-2 rounded-md p-2 transition-colors hover:bg-gray-100"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            plain
          >
            <ChevronRightIcon
              className={`h-5 w-5 transform text-gray-500 transition-transform ${!isCollapsed ? "rotate-180" : ""}`}
            />
          </Button>
        </header>

        {filters && !isCollapsed ? (
          <div className="flex w-full flex-wrap gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-600">
            {filters}
          </div>
        ) : null}

        {isCollapsed ? (
          <div className="flex h-full flex-col items-center border-r border-gray-200 bg-gray-50 py-4">
            {createRoute ? (
              <Link title="Create new" to={createRoute} className="mb-4">
                <div className="focus:outline-hidden rounded-full bg-blue-50 p-2 transition duration-200 ease-in-out hover:bg-blue-100 focus:ring-2 focus:ring-blue-500">
                  <PlusIcon className="h-5 w-5 text-blue-600" />
                </div>
              </Link>
            ) : null}
            <div className="mt-2 flex flex-col items-center space-y-3">
              {totalCount > 0 ? (
                <div className="flex flex-col items-center">
                  <span className="text-xs font-medium text-gray-500">
                    {totalCount}
                  </span>
                  <span className="text-xs text-gray-400">items</span>
                </div>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
              )}
            </div>
          </div>
        ) : totalCount > 0 ? (
          <nav className="relative isolate flex h-full flex-col overflow-y-auto border-r border-gray-200 bg-gray-50">
            <ul className="divide-y divide-gray-200">
              {items.map((item: any, index: number, arr: T[]) => (
                <li
                  key={item.id}
                  ref={index === arr.length - 1 ? ref : undefined}
                  className="focus-visible:outline-hidden transition duration-150"
                >
                  <Link
                    className="focus-visible:outline-hidden block overflow-hidden rounded-md p-3 transition-all duration-150 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 hover:bg-blue-50"
                    activeProps={{
                      className: "bg-blue-50 border-l-8 border-blue-500",
                    }}
                    {...linkProps(item)}
                  >
                    {({ isActive }) => {
                      return (
                        <BaseSidebarListItem isActive={isActive}>
                          {itemNode(item)}
                        </BaseSidebarListItem>
                      );
                    }}
                  </Link>
                </li>
              ))}
            </ul>

            {isFetchingNextPage ? (
              <div className="sticky bottom-4 z-10 flex w-full justify-center">
                <div className="backdrop-blur-xs flex space-x-3 rounded-md bg-gray-800/70 p-2 text-gray-50 shadow-lg">
                  <Spinner className="h-4 w-4 text-white" />
                  <span className="text-sm">Loading more...</span>
                </div>
              </div>
            ) : null}
          </nav>
        ) : (
          <EmptyState listRoute={listRoute} isCollapsed={isCollapsed} />
        )}
      </aside>
      <article className="h-full min-w-0 grow bg-white">
        <GenericErrorBoundary>
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center">
                <div className="flex flex-col items-center">
                  <Spinner className="h-8 w-8 text-blue-500" />
                  <p className="mt-4 text-sm text-gray-500">
                    Loading content...
                  </p>
                </div>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </GenericErrorBoundary>
      </article>
    </div>
  );
}

export function BaseSidebarListItem({
  isActive,
  children,
}: {
  isActive: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, [isActive, ref]);

  return <div ref={ref}>{children}</div>;
}

function EmptyState({
  listRoute,
  isCollapsed = false,
}: {
  listRoute: any;
  isCollapsed?: boolean;
}) {
  const search = useSearch({ from: listRoute.id });
  const searchCount = Object.keys(search).length;

  if (isCollapsed) {
    return (
      <div className="flex h-full flex-col items-center justify-center border-r border-gray-200 bg-gray-50 p-2 text-center">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center border-r border-gray-200 bg-gray-50 p-6 text-center">
      <div className="w-1/2 opacity-80">
        <IllustrationIdea />
      </div>

      <h3 className="mt-4 text-sm font-medium text-gray-900">
        No results found
      </h3>
      <p className="mt-2 text-sm text-gray-500">
        {searchCount === 0 ? (
          "No results match your search criteria."
        ) : (
          <Link
            className="flex items-center text-blue-600 underline hover:text-blue-800"
            to={listRoute}
            search={undefined}
          >
            <span>
              Clear {searchCount} filter{searchCount > 1 ? "s" : ""}
            </span>
          </Link>
        )}
      </p>
    </div>
  );
}
